import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ─── Auth Modal ────────────────────────────────────────────────────────────────

function AuthModal({ mode, onClose }: { mode: 'login' | 'register'; onClose: () => void }) {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email || !password || (!isLogin && !name)) { setError('Please fill all fields'); return; }
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal card */}
      <div className="relative z-10 w-full max-w-md bg-slate-900/95 border border-white/10 rounded-3xl shadow-2xl p-8">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4">
            <span className="text-2xl">🏙️</span>
          </div>
          <h2 className="text-2xl font-black text-white">{isLogin ? 'Welcome back' : 'Join Samatvam'}</h2>
          <p className="text-slate-400 text-sm mt-1">{isLogin ? 'Continue building your cities' : 'Start your chain of cities'}</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-400/20 text-red-300 p-3 rounded-xl mb-4 text-sm">{error}</div>
        )}

        <div className="space-y-3">
          {!isLogin && (
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wide">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 transition-all"
                placeholder="Your name"
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wide">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSubmit(); }}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 transition-all"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wide">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSubmit(); }}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 transition-all"
              placeholder="••••••••"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white py-3 rounded-xl font-bold text-base transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-amber-500/20 hover:shadow-xl transform hover:scale-105 active:scale-95 mt-2"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isLogin ? 'Signing in...' : 'Creating...'}
              </span>
            ) : isLogin ? 'Enter Samatvam' : 'Start Building'}
          </button>

          {!isLogin && (
            <div className="bg-amber-500/10 border border-amber-400/20 rounded-xl p-3 text-center">
              <p className="text-amber-300 text-xs font-medium">New builders start with <span className="font-bold">100 URJA</span> energy</p>
            </div>
          )}
        </div>

        <div className="mt-5 text-center">
          <button
            onClick={() => { setIsLogin(!isLogin); setError(''); }}
            className="text-slate-400 hover:text-amber-300 text-sm transition-all"
          >
            {isLogin ? "No account? " : "Already a builder? "}
            <span className="underline text-amber-400">{isLogin ? 'Create one' : 'Sign In'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Landing Page ──────────────────────────────────────────────────────────────

const Landing: React.FC = () => {
  const [modal, setModal] = useState<'login' | 'register' | null>(null);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">

      {/* ── Hero ── */}
      <div className="relative h-screen min-h-[600px] overflow-hidden">

        {/* Split city images */}
        <img src="/samatvam/city.jpg" alt="" className="absolute top-0 left-0 h-full object-cover object-center pointer-events-none"
          style={{ width: '52%', WebkitMaskImage: 'linear-gradient(to right, black 92%, transparent 100%)', maskImage: 'linear-gradient(to right, black 92%, transparent 100%)' }} />
        <img src="/samatvam/city2.jpg" alt="" className="absolute top-0 right-0 h-full object-cover object-center pointer-events-none"
          style={{ width: '52%', WebkitMaskImage: 'linear-gradient(to left, black 92%, transparent 100%)', maskImage: 'linear-gradient(to left, black 92%, transparent 100%)' }} />
        <div className="absolute top-0 h-full pointer-events-none"
          style={{ left: '48%', width: '4%', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%)', maskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%)' }}></div>

        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-900/20"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-slate-950/30"></div>

        {/* Nav */}
        <nav className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-6 z-30">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-amber-500/20 backdrop-blur-sm border border-amber-400/30 rounded-xl flex items-center justify-center">
              <span className="text-lg">🏙️</span>
            </div>
            <span className="font-black text-lg tracking-tight">Samatvam</span>
          </div>
          <button
            onClick={() => setModal('login')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-5 py-2 rounded-xl text-sm font-semibold transition-all"
          >
            Sign In
          </button>
        </nav>

        {/* Hero text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-10">
          <div className="inline-flex items-center space-x-2 bg-amber-500/10 border border-amber-400/20 px-4 py-1.5 rounded-full mb-6">
            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></div>
            <span className="text-amber-300 text-xs font-semibold tracking-widest uppercase">Sustainable City Building Digital Twin</span>
          </div>

          <h1 className="text-6xl md:text-8xl font-black tracking-tight mb-4 leading-none">
            <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-amber-400 bg-clip-text text-transparent">
              Samatvam
            </span>
          </h1>

          <p className="text-slate-300 text-xl md:text-2xl font-light max-w-xl mb-4 leading-relaxed">
            A desert wasteland.<br />
            <span className="text-white font-semibold">Your strategy. A chain of cities.</span>
          </p>

          <p className="text-slate-400 text-base max-w-lg mb-10 leading-relaxed">
            Every action in the real world shapes this digital twin. Build sustainably — watch your cities grow.
          </p>

          {/* Season 1 badge */}
          <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-400/20 px-4 py-1.5 rounded-full mb-5">
            <div className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse"></div>
            <span className="text-orange-300 text-xs font-bold tracking-widest uppercase">Season 1 · Live</span>
          </div>

          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => setModal('register')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white px-10 py-4 rounded-2xl font-black text-lg shadow-2xl hover:shadow-amber-500/30 transition-all duration-300 transform hover:scale-105 active:scale-95"
            >
              Claim Your Land →
            </button>
            <button
              onClick={() => setModal('login')}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/25 text-white px-10 py-4 rounded-2xl font-black text-lg shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95"
            >
              Sign In
            </button>
          </div>

          <p className="text-slate-500 text-sm mt-4 mb-20">1 WAX per plot · Powered by WAX blockchain</p>
        </div>

        {/* ── Quickstart Card — bottom right of hero ── */}
        <div className="absolute bottom-14 right-8 w-72 z-20 hidden lg:block">
          <div className="relative bg-slate-900/90 backdrop-blur-md border border-amber-400/25 rounded-2xl p-5 shadow-2xl shadow-amber-500/10 overflow-hidden">
            {/* Pulsing border glow */}
            <div className="absolute -inset-px rounded-2xl border border-amber-400/40 animate-pulse pointer-events-none" />
            {/* Amber corner accent */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-amber-500/5 rounded-bl-full pointer-events-none" />

            <div className="flex items-center space-x-2 mb-3">
              <div className="w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md shadow-amber-500/30">
                <span className="text-xs">⚡</span>
              </div>
              <h3 className="text-white font-black text-sm tracking-tight">Quickstart</h3>
              <span className="ml-auto text-[9px] font-bold uppercase tracking-widest bg-amber-500/15 border border-amber-400/25 text-amber-300 px-2 py-0.5 rounded-full">Season Zero</span>
            </div>

            <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide mb-2.5">What you can do right now:</p>

            <ul className="space-y-2">
              <li className="flex items-start space-x-2.5">
                <span className="text-amber-400 text-xs mt-0.5 flex-shrink-0">▸</span>
                <span className="text-slate-300 text-xs leading-relaxed">
                  Buy land grids — <span className="text-amber-400 font-bold">1 WAX each</span>
                </span>
              </li>
              <li className="flex items-start space-x-2.5">
                <span className="text-amber-400 text-xs mt-0.5 flex-shrink-0">▸</span>
                <span className="text-slate-300 text-xs leading-relaxed">
                  One random land grid wins <span className="text-amber-400 font-bold">WAX / NFT prizes</span> every day
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 animate-bounce">
          <span className="text-slate-500 text-xs tracking-widest uppercase">Discover</span>
          <div className="w-px h-8 bg-gradient-to-b from-slate-500 to-transparent"></div>
        </div>
      </div>

      {/* ── Feature Cards ── */}
      <div className="px-6 py-20 max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Not just a game.<br />
            <span className="text-amber-400">A living city.</span>
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">Samatvam mirrors real-world sustainable activities. As your companies grow, your city grows.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: '🌱',
              title: 'Digital Twin',
              color: 'from-green-500/20 to-emerald-500/10',
              border: 'border-green-500/20',
              accent: 'text-green-400',
              desc: 'Real-world activities — solar installations, courses, registrations — directly shape your city\'s growth and resources.'
            },
            {
              icon: '⬡',
              title: 'WAX Economy',
              color: 'from-blue-500/20 to-cyan-500/10',
              border: 'border-blue-500/20',
              accent: 'text-blue-400',
              desc: 'WAX blockchain handles all real payments. Once inside, your city runs on URJA energy and AHN currency — fast and free.'
            },
            {
              icon: '⚖️',
              title: 'Samatvam — Balance',
              color: 'from-amber-500/20 to-orange-500/10',
              border: 'border-amber-500/20',
              accent: 'text-amber-400',
              desc: 'The name means equilibrium. Build too fast without balance and your city degrades. Sustainable choices unlock rare technologies.'
            }
          ].map((f, i) => (
            <div key={i} className={`bg-gradient-to-br ${f.color} border ${f.border} rounded-2xl p-6 hover:scale-105 transition-transform duration-300`}>
              <div className="text-4xl mb-4">{f.icon}</div>
              <h3 className={`text-lg font-black mb-2 ${f.accent}`}>{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Season Zero Land Sale ── */}
      <div className="px-6 py-16 max-w-5xl mx-auto">
        <div className="relative bg-gradient-to-br from-amber-950/80 via-slate-900/90 to-slate-900/80 border border-amber-500/20 rounded-3xl p-8 md:p-12 overflow-hidden">

          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange-500/5 rounded-full blur-3xl"></div>
          </div>

          <div className="relative flex flex-col md:flex-row items-center gap-10">

            {/* Left — text */}
            <div className="flex-1">
              <div className="inline-flex items-center space-x-2 bg-amber-500/15 border border-amber-400/25 px-3 py-1 rounded-full mb-4">
                <div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></div>
                <span className="text-amber-300 text-xs font-bold tracking-widest uppercase">Season Zero — Now Live</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-black text-white mb-3 leading-tight">
                Claim your plot of<br />
                <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
                  desert land
                </span>
              </h2>

              <p className="text-slate-300 text-base leading-relaxed mb-5">
                The city starts as a barren wasteland. Every WAX sent to Samatvam permanently assigns you a plot of land on the grid — spiralling outward from the city origin at (0,0).
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { label: 'Price per plot', value: '1 WAX', color: 'text-amber-400' },
                  { label: 'Starting point', value: '(0, 0)', color: 'text-teal-400' },
                  { label: 'Expansion', value: 'Spiral outward', color: 'text-blue-400' },
                  { label: 'Season', value: 'Zero', color: 'text-orange-400' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-white/5 border border-white/8 rounded-xl px-4 py-3">
                    <p className="text-slate-500 text-xs uppercase tracking-wide mb-0.5">{stat.label}</p>
                    <p className={`font-black text-lg ${stat.color}`}>{stat.value}</p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setModal('register')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white px-8 py-3.5 rounded-xl font-black text-base shadow-xl shadow-amber-500/20 hover:shadow-amber-500/30 transition-all duration-300 transform hover:scale-105 active:scale-95"
              >
                Register &amp; Claim Land →
              </button>
            </div>

            {/* Right — mini grid preview */}
            <div className="flex-shrink-0">
              <p className="text-slate-500 text-xs uppercase tracking-widest text-center mb-3">City Grid Preview</p>
              <div className="inline-grid gap-1" style={{ gridTemplateColumns: 'repeat(7, 36px)' }}>
                {(() => {
                  // Show a 7x7 preview grid centred on (0,0)
                  const spiral: [number,number][] = [];
                  const n = 20;
                  if (n > 0) {
                    spiral.push([0,0]);
                    let x=0,y=0,step=1,dir=0;
                    const dx=[1,0,-1,0],dy=[0,1,0,-1];
                    while (spiral.length < n) {
                      for (let rep=0;rep<2&&spiral.length<n;rep++) {
                        for (let i=0;i<step&&spiral.length<n;i++) { x+=dx[dir];y+=dy[dir];spiral.push([x,y]); }
                        dir=(dir+1)%4;
                      }
                      step++;
                    }
                  }
                  const claimedSet = new Set(spiral.map(([x,y])=>`${x},${y}`));
                  const rows = [];
                  for (let gy=3;gy>=-3;gy--) {
                    for (let gx=-3;gx<=3;gx++) {
                      const key=`${gx},${gy}`;
                      const idx = spiral.findIndex(([x,y])=>x===gx&&y===gy);
                      const isOrigin = gx===0&&gy===0;
                      const isClaimed = claimedSet.has(key);
                      rows.push(
                        <div
                          key={key}
                          className={[
                            'w-9 h-9 rounded-md border flex items-center justify-center',
                            isOrigin ? 'bg-amber-500 border-amber-400 shadow-md shadow-amber-500/40'
                              : isClaimed ? 'bg-amber-900/60 border-amber-700/40'
                              : 'bg-slate-800/60 border-white/5'
                          ].join(' ')}
                        >
                          {isOrigin
                            ? <span className="text-[7px] font-black text-white">0,0</span>
                            : isClaimed
                              ? <span className="text-[7px] text-amber-400/70 font-semibold">{idx+1}</span>
                              : <span className="text-slate-700 text-[9px]">·</span>
                          }
                        </div>
                      );
                    }
                  }
                  return rows;
                })()}
              </div>
              <p className="text-slate-600 text-[10px] text-center mt-2">
                🟧 claimed &nbsp;·&nbsp; ⬛ available
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Roadmap ── */}
      <div className="px-6 py-20 max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center space-x-2 bg-amber-500/10 border border-amber-400/20 px-4 py-1.5 rounded-full mb-4">
            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></div>
            <span className="text-amber-300 text-xs font-bold tracking-widest uppercase">Roadmap</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white">
            From desert to <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">living city</span>
          </h2>
        </div>

        <div className="relative">
          {/* Vertical spine */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-amber-500/60 via-amber-500/20 to-transparent hidden md:block"></div>

          <div className="space-y-6">

            {/* Phase 0 — LIVE */}
            <div className="relative md:pl-16">
              <div className="hidden md:flex absolute left-0 top-5 w-12 h-12 items-center justify-center">
                <div className="w-3 h-3 bg-amber-400 rounded-full shadow-lg shadow-amber-400/50 z-10 relative">
                  <div className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-60"></div>
                </div>
              </div>
              <div className="relative bg-gradient-to-br from-amber-950/70 via-slate-800/80 to-slate-900/70 border border-amber-400/30 rounded-2xl p-6 overflow-hidden shadow-lg shadow-amber-500/10"
                style={{ animation: 'none' }}>
                <div className="absolute inset-0 rounded-2xl border border-amber-400/20 pointer-events-none" style={{ animation: 'pulse 3s ease-in-out infinite' }}></div>
                <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="text-xs font-black uppercase tracking-widest bg-amber-500 text-white px-3 py-1 rounded-full shadow-md shadow-amber-500/30">Season Zero</span>
                  <span className="text-xs font-black uppercase tracking-widest bg-green-500/20 border border-green-400/30 text-green-300 px-3 py-1 rounded-full flex items-center space-x-1.5">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block animate-pulse"></span>
                    <span>Phase 0 — Live Now</span>
                  </span>
                </div>
                <h3 className="text-xl font-black text-white mb-3">Land Sale &amp; Daily Prize Draw</h3>
                <ul className="space-y-2">
                  {[
                    'Sale of 1,000 desert land plots — 1 WAX each',
                    'Plots assigned in a clockwise spiral from city origin (0,0)',
                    'One random land grid wins WAX / NFT prizes every day',
                    'Prize automatically sent to the holder\'s WAX / Ahantravyuh wallet',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-2.5">
                      <span className="text-amber-400 text-sm mt-0.5 flex-shrink-0">◆</span>
                      <span className="text-slate-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Phase 1 — Coming Soon */}
            <div className="relative md:pl-16 opacity-60">
              <div className="hidden md:flex absolute left-0 top-5 w-12 h-12 items-center justify-center">
                <div className="w-3 h-3 bg-slate-500 rounded-full border-2 border-slate-400/30 z-10 relative"></div>
              </div>
              <div className="bg-slate-800/50 border border-slate-600/30 rounded-2xl p-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="text-xs font-black uppercase tracking-widest bg-slate-700/60 border border-slate-500/30 text-slate-300 px-3 py-1 rounded-full">Season Zero</span>
                  <span className="text-xs font-black uppercase tracking-widest bg-blue-500/10 border border-blue-400/20 text-blue-300 px-3 py-1 rounded-full">Phase 1 — Coming Soon</span>
                </div>
                <h3 className="text-xl font-black text-white mb-3">City Building Begins</h3>
                <ul className="space-y-2">
                  {[
                    'Place structures on your owned plots using URJA energy',
                    'Unlock roads, housing, solar panels, and community hubs',
                    'Real-world sustainability actions generate in-game URJA',
                    'Inter-player trading of land and city materials',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-2.5">
                      <span className="text-slate-500 text-sm mt-0.5 flex-shrink-0">◇</span>
                      <span className="text-slate-400 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Season 1 — Future */}
            <div className="relative md:pl-16 opacity-35">
              <div className="hidden md:flex absolute left-0 top-5 w-12 h-12 items-center justify-center">
                <div className="w-3 h-3 bg-slate-600 rounded-full border border-slate-500/20 z-10 relative"></div>
              </div>
              <div className="bg-slate-900/40 border border-slate-700/20 rounded-2xl p-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="text-xs font-black uppercase tracking-widest bg-purple-500/10 border border-purple-400/20 text-purple-300 px-3 py-1 rounded-full">Season One</span>
                  <span className="text-xs font-black uppercase tracking-widest bg-slate-700/30 border border-slate-600/20 text-slate-500 px-3 py-1 rounded-full">Future</span>
                </div>
                <h3 className="text-xl font-black text-white mb-3">Full City Mechanics</h3>
                <ul className="space-y-2">
                  {[
                    'Dynamic city simulation — pollution, power grids, water systems',
                    'Population happiness linked to real sustainability metrics',
                    'Collaborative districts and cooperative guilds',
                    'Atavi endgame: reach the living forest city',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-2.5">
                      <span className="text-slate-600 text-sm mt-0.5 flex-shrink-0">◇</span>
                      <span className="text-slate-500 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Journey Strip ── */}
      <div className="px-6 py-16 bg-slate-900/50 border-y border-white/5">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-slate-500 text-xs uppercase tracking-widest mb-10">The journey</p>
          <div className="flex items-center justify-between relative">
            <div className="absolute top-5 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
            {[
              { icon: '🏜️', label: 'Desert Wasteland', sub: 'Where you begin' },
              { icon: '🌿', label: 'Padra', sub: 'First settlement' },
              { icon: '🏘️', label: 'Gram', sub: 'Village grows' },
              { icon: '🏙️', label: 'Nagara', sub: 'City emerges' },
              { icon: '🌳', label: 'Atavi', sub: 'Forest city — the goal' },
            ].map((step, i) => (
              <div key={i} className="flex flex-col items-center relative z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl mb-3 ${i === 0 ? 'bg-slate-700 border-2 border-amber-500/50' : 'bg-slate-800 border border-white/10'}`}>
                  {step.icon}
                </div>
                <p className="text-white text-xs font-bold text-center hidden md:block">{step.label}</p>
                <p className="text-slate-500 text-xs text-center hidden md:block">{step.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA Bottom ── */}
      <div className="px-6 py-24 text-center">
        <h2 className="text-4xl font-black mb-4">Ready to build?</h2>
        <p className="text-slate-400 mb-8 max-w-md mx-auto">Join the first sustainable city building game connected to real-world impact.</p>
        <button
          onClick={() => setModal('register')}
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white px-12 py-4 rounded-2xl font-black text-lg shadow-2xl hover:shadow-amber-500/30 transition-all duration-300 transform hover:scale-105 active:scale-95"
        >
          Start Building — It's Free
        </button>
      </div>

      {/* Auth Modal */}
      {modal && <AuthModal mode={modal} onClose={() => setModal(null)} />}
    </div>
  );
};

export default Landing;
